import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { LocalStorage } from "@faire/web--source/common/Storage";
import { Button } from "@faire/web--source/slate/Button";
import Checkbox from "@faire/web--source/slate/Checkbox";
import { CheckOutline } from "@faire/web--source/slate/icons/foundation/CheckOutline";
import { Information } from "@faire/web--source/slate/icons/Information";
import { FormInputLabel, Input } from "@faire/web--source/slate/Input";
import {
  Dropdown,
  IOption,
  IProps as IDropdownProps,
} from "@faire/web--source/slate/Input/Dropdown";
import { Column, Grid, Row } from "@faire/web--source/slate/Layout";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Tooltip } from "@faire/web--source/slate/Tooltip";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContent } from "@faire/web--source/ui/ResponsiveContent";
import { IBrandLead } from "@faire/web-api--source/indigofair/data/IBrandLead";
import { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import brands from "@faire/web-api--source/routes/brands";
import { observer } from "mobx-react";
import * as React from "react";
import type { JSX } from "react";
import styled, { DefaultTheme } from "styled-components";

import { BareProgressBar } from "@faire/retailer/components/BareProgressBar/BareProgressBar";
import { NUM_PRODUCTS_IOPTIONS } from "@faire/retailer/consts/numProductsOptions";
import { SOURCE_OPTIONS } from "@faire/retailer/consts/sourceOptions";
import { STOCKIST_IOPTIONS } from "@faire/retailer/consts/stockistOptions";
import { INBOUND_BRAND_CATEGORY_OPTIONS } from "@faire/retailer/features/MakerApplication/MakerApplicationCategories";
import { useSettingEnableMakerSignUpHooksMigration } from "@faire/retailer/settings/useSettingEnableMakerSignUpHooksMigration";
import { MakerSignUpWrapper } from "@faire/retailer/views/apply/MakerApplication/MakerSignUp/MakerSignUpWrapper";
import { MakerSignUpState } from "@faire/retailer/views/apply/MakerApplication/MakerSignUpState";

import { AcceptTermsCheckbox } from "../BrandCreateAccount/AcceptTermsCheckbox";
import { AcceptTermsMessage } from "../BrandCreateAccount/AcceptTermsMessage";
import { MAKER_SIGN_UP_BASED_IN_STORAGE_KEY } from "../consts";

import {
  getLabels,
  HEAR_ABOUT_US_LABEL,
  NUMBER_OF_PRODUCTS_LABEL,
} from "./consts";
import { MakerSignUpFooterWrapper } from "./MakerSignUpFooterWrapper";
import { useMakerSignUpFormFields } from "./useMakerSignUpFormFields";

const NoWrapFormatting = (chunk: JSX.Element) => (
  <div style={{ whiteSpace: "nowrap", display: "inline-block" }}>{chunk}</div>
);
interface IProps {
  viewState: MakerSignUpState;
}

const MakerSignUpFormBase: React.FC<IProps> = ({ viewState }) => {
  const { strictLocalize } = useStrictLocalization();
  const enableMakerSignUpHooksMigration =
    useSettingEnableMakerSignUpHooksMigration();
  const {
    companyName,
    countryLabel,
    countryDropdownOption,
    setCountry,
    url,
    email,
    categoryOption,
    stockists,
    numProducts,
    displaySource,
    onUrlBlur,
    errorFor,
    urlValidationInProgress,
    isUrlValid,
    onChange,
    onSubmit,
    onBackClick,
    brandNameEvent,
    acceptedTerms,
    toggleAcceptedTerms,
    shouldShowSMSOptIn,
    emailEvent,
    categoryEvent,
    setFormField,
    returnEvent,
    sourceEvent,
    countryEvent,
    secondaryCategoryOptions,
    selectedProductCategory,
    shouldCollectSecondaryProductCategory,
    numberStoresEvent,
  } = useMakerSignUpFormFields(viewState);

  const labels = getLabels(strictLocalize);

  const viewport = useViewport();

  const CompanyNameInput = (
    <>
      <Input
        label={labels["brand_name"]}
        error={errorFor("companyName")}
        value={companyName}
        name="companyName"
        onChange={(e) => {
          brandNameEvent();
          onChange(e);
        }}
        data-test-id="companyName"
        autoComplete="organization"
      />
      <Spacer size="2x" />
    </>
  );

  const BusinessWebsiteInput = (
    <>
      <Input
        label={labels["website_url"]}
        defaultValue={url}
        onBlur={onUrlBlur}
        error={errorFor("url")}
        suffixNode={
          <>
            {urlValidationInProgress ? <LoadingSpinner size="small" /> : null}
            {isUrlValid ? <CheckOutline /> : null}
          </>
        }
        autoComplete="url"
        data-test-id="url"
      />
      <Spacer size="2x" />
    </>
  );

  const EmailAddressInput = (
    <>
      <Input
        label={labels["email_address"]}
        error={errorFor("email")}
        name="email"
        value={email}
        onChange={(e) => {
          emailEvent();
          onChange(e);
        }}
        data-test-id="email"
        autoComplete="email"
      />
      <Spacer size="2x" />
    </>
  );

  const ProductCategoryDropdown = (
    <>
      <FlexDropdown
        label={labels["primary_category"]}
        options={INBOUND_BRAND_CATEGORY_OPTIONS(strictLocalize)}
        onSelect={(value) => {
          categoryEvent();
          setFormField("categoryOption", value);
          secondaryCategoryOptions.clear();
        }}
        value={categoryOption?.value}
        error={errorFor("categoryOption")}
        data-test-id="category"
        disableInput
      />
      <Spacer size="2x" />
    </>
  );

  const SecondaryProductCategorySelect =
    shouldCollectSecondaryProductCategory ? (
      <>
        <FormInputLabel>
          <WrappingRow>
            {labels["secondary_category"]}
            <StrictLocalMsg
              defaultMessage="<NoWrapFormatting>(select all that apply)</NoWrapFormatting>"
              description={{
                text: "Text indicating that this field is multi-select and optional",
              }}
              values={{
                NoWrapFormatting,
              }}
            />
          </WrappingRow>
        </FormInputLabel>
        <Spacer size="2x" />
        <Grid columns={viewport.isMobile ? 1 : 2} gap="2x">
          {selectedProductCategory!.subCategories.map((x) => (
            <Checkbox
              onChange={() => {
                if (enableMakerSignUpHooksMigration) {
                  const newCategories = new Set(secondaryCategoryOptions);
                  if (newCategories.has(x.value)) {
                    newCategories.delete(x.value);
                  } else {
                    newCategories.add(x.value);
                  }
                  setFormField("secondaryCategoryOptions", newCategories);
                } else {
                  secondaryCategoryOptions.has(x.value)
                    ? secondaryCategoryOptions.delete(x.value)
                    : secondaryCategoryOptions.add(x.value);
                }
              }}
              checked={secondaryCategoryOptions.has(x.value)}
              key={x.value}
              label={x.label}
            />
          ))}
        </Grid>
        <Spacer size="2x" />
      </>
    ) : null;

  const IndependentStoreDropdown = (
    <>
      <FlexDropdown
        label={labels["number_of_stores"]}
        value={stockists?.toString()}
        options={STOCKIST_IOPTIONS(strictLocalize)}
        onSelect={(option: IOption) => {
          numberStoresEvent();
          setFormField("stockists", parseInt(option.value));
        }}
        error={errorFor("stockists")}
        data-test-id="stockists"
        disableInput
      />
      <Spacer size="2x" />
    </>
  );

  const HearAboutUsDropdown = (
    <>
      <FlexDropdown<React.FC<IDropdownProps<IBrandLead.Source>>>
        label={HEAR_ABOUT_US_LABEL(strictLocalize)}
        value={displaySource}
        options={SOURCE_OPTIONS(strictLocalize)}
        onSelect={(option: IOption<IBrandLead.Source>) => {
          sourceEvent();
          setFormField("_source", option.value);
        }}
        data-test-id="source"
        disableInput
      />
      <Spacer size="2x" />
    </>
  );

  const LocationDropdown = (
    <>
      <Spacer size="1x" />
      <Row>
        <Typography>
          <StrictLocalMsg
            defaultMessage="Country of tax residence"
            description={{
              text: "country where brand is located in for tax purposes",
            }}
          />
        </Typography>
        <Spacer size="1x" />
        <Tooltip
          contents={[
            {
              body: strictLocalize({
                defaultMessage:
                  "This is the country in which your business is managed or incorporated, and files income tax returns.",
                description: {
                  text: "Tooltip explaining what country of tax residence means -- where your business is managed or incorporated, where income tax returns will be filed under",
                },
              }),
            },
          ]}
          icon={
            <Information color={Core.text.subdued} width="16px" height="16px" />
          }
        />
      </Row>
      <Dropdown
        data-test-id="country"
        options={countryDropdownOption}
        value={countryLabel}
        onSelect={(option: IOption<string>) => {
          LocalStorage.get().setItem(
            MAKER_SIGN_UP_BASED_IN_STORAGE_KEY,
            option.value
          );
          countryEvent();
          setCountry({
            isoCode: option.value as ISO3166Alpha3,
            displayName: option.label,
          });
        }}
        error={errorFor("country.isoCode")}
      />
      <Spacer size="2x" />
    </>
  );

  const NumberOfProductsDropdown = (
    <>
      <FlexDropdown
        label={NUMBER_OF_PRODUCTS_LABEL(strictLocalize)}
        value={numProducts?.toString()}
        options={NUM_PRODUCTS_IOPTIONS(strictLocalize)}
        onSelect={(option: IOption) => {
          setFormField("numProducts", parseInt(option.value));
        }}
        error={errorFor("numProducts")}
        data-test-id="numProducts"
        disableInput
      />
      <Spacer size="2x" />
    </>
  );

  const renderSignupForm = () => (
    <>
      {CompanyNameInput}
      {EmailAddressInput}
      {BusinessWebsiteInput}
      {LocationDropdown}
      {NumberOfProductsDropdown}
      {IndependentStoreDropdown}
      {ProductCategoryDropdown}
      {SecondaryProductCategorySelect}
      {HearAboutUsDropdown}
    </>
  );

  const footerDesktopComponent = (
    <>
      <CTAButton
        disabled={!shouldShowSMSOptIn ? !acceptedTerms : false}
        variant="primary"
        onClick={onSubmit}
        data-test-id="submit"
      >
        <StrictLocalMsg
          defaultMessage="Continue"
          description={{
            text: "CTA for user to continue with the sign-up process",
          }}
        />
      </CTAButton>
      <Spacer height="3x" />
      {!shouldShowSMSOptIn ? (
        <Row align="flex-start">
          <Button variant="tertiary" onClick={returnEvent} linkTo={brands()}>
            <StrictLocalMsg
              defaultMessage="Return to Faire.com"
              description={{ text: "Link that returns to faire.com homepage" }}
            />
          </Button>
        </Row>
      ) : null}
    </>
  );

  const renderFooterMobileComponent = () => {
    const footerButtonGroup = (
      <>
        <Row
          align="center"
          justify="center"
          style={{ padding: getSpacing("3x", 0) }}
        >
          <CTAButton
            variant="secondary"
            onClick={onBackClick}
            data-test-id="back"
          >
            <StrictLocalMsg
              defaultMessage="Back"
              description={{
                text: "Link that returns to faire.com homepage",
              }}
            />
          </CTAButton>
          <Spacer width="2x" />
          <CTAButton
            disabled={!shouldShowSMSOptIn ? !acceptedTerms : false}
            variant="primary"
            onClick={onSubmit}
            data-test-id="submit"
          >
            <StrictLocalMsg
              defaultMessage="Continue"
              description={{
                text: "CTA for user to continue with the sign-up process",
              }}
            />
          </CTAButton>
        </Row>
      </>
    );
    return (
      <MakerSignUpFooterWrapper>
        <Row>
          <BareProgressBar $progress={0} $height="2px" />
        </Row>
        {footerButtonGroup}
      </MakerSignUpFooterWrapper>
    );
  };

  return (
    <MakerSignUpWrapper>
      <FormHeaderContentWrapper>
        <>
          <Spacer size="2x" />

          <Typography variant="displaySSerifRegular">
            <StrictLocalMsg
              defaultMessage="Grow your wholesale business"
              description={{
                text: "A propsective brand can grow their wholesale business by joining the Faire platform by signing up to join Faire",
              }}
            />
          </Typography>

          <Spacer size="2x" />

          <Typography>
            <StrictLocalMsg
              defaultMessage="Let's start with a few details"
              description={{
                text: "A form to get more details about a prospective brand to sign-up to sell on Faire",
              }}
            />
          </Typography>
        </>
      </FormHeaderContentWrapper>
      <FormContentWrapper>
        <Spacer size="2x" />
        {renderSignupForm()}
      </FormContentWrapper>

      <FormContentWrapper>
        <Spacer size="1x" />

        {!shouldShowSMSOptIn ? (
          <AcceptTermsCheckbox
            hasError={false}
            value={acceptedTerms}
            onChange={() => toggleAcceptedTerms()}
          />
        ) : null}

        {errorFor("error") ? (
          <>
            <Typography color={Core.text.critical}>
              {errorFor("error")}
            </Typography>
            <Spacer size="2x" />
          </>
        ) : null}

        <ResponsiveContent
          desktop={footerDesktopComponent}
          mobile={renderFooterMobileComponent()}
        />

        {shouldShowSMSOptIn ? (
          <>
            <Spacer size="1x" />
            <AcceptTermsMessage />
          </>
        ) : null}
      </FormContentWrapper>
    </MakerSignUpWrapper>
  );
};

export const MakerSignUpForm = observer(MakerSignUpFormBase);

const CTAButton = styled(Button)`
  width: fit-content;
  min-width: 250px;

  ${responsiveMediaQueries.mobile} {
    width: 42%;
    min-width: 100px;
  }
`;

const FlexDropdown = styled(Dropdown).attrs({
  theme: { input: { label: { color: Core.text.default } } } as DefaultTheme,
})`
  flex: 1;
`;

const FormContentWrapper = styled(Column)`
  width: 100%;

  ${responsiveMediaQueries.mobile} {
    background-color: ${Core.surface.inverse};
    padding: 0 ${getSpacing("3x")};
  }
`;

const FormHeaderContentWrapper = styled(Column)`
  ${responsiveMediaQueries.mobile} {
    padding: 0 ${getSpacing("3x")};
  }
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- move gap to attrs
const WrappingRow = styled(Row)`
  flex-flow: wrap;
  gap: 0 4px;
`;
